import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * PDF Export Utility for MaceSuite
 * Generates professional PDFs from HTML content or structured data
 * All export functions return { success: boolean, filename?: string, blobSize?: number, error?: string }
 */

const COMPANY_NAME = 'MaceSuite AIS';
const PAGE_MARGIN = 15;
const LINE_HEIGHT = 7;

/**
 * Helper: Save PDF and log blob size
 */
function savePDFWithLogging(pdf, filename) {
    try {
        const pdfBlob = pdf.output('blob');
        const blobSize = pdfBlob.size;
        console.log(`[PDF Export] ${filename} - Blob size: ${blobSize} bytes (${(blobSize / 1024).toFixed(2)} KB)`);
        
        // Validate PDF has content (not blank)
        if (blobSize < 1000) {
            console.warn(`[PDF Export] Warning: ${filename} has very small size (${blobSize} bytes), may be blank`);
        }
        
        pdf.save(filename);
        return { success: true, filename, blobSize };
    } catch (error) {
        console.error(`[PDF Export] Failed to save ${filename}:`, error);
        return { success: false, filename, error: error.message };
    }
}

/**
 * Export HTML element to PDF using html2canvas
 */
export async function exportElementToPDF(elementId, filename, title) {
    try {
        const element = document.getElementById(elementId);
        if (!element) {
            const error = `Element with id '${elementId}' not found`;
            console.error(`[PDF Export] ${error}`);
            return { success: false, filename, error };
        }

        console.log(`[PDF Export] Starting capture of element: ${elementId}`);
        
        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        const imgWidth = pageWidth - (PAGE_MARGIN * 2);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Add header
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(title || 'Report', PAGE_MARGIN, 15);
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Generated: ${new Date().toLocaleString()}`, PAGE_MARGIN, 22);
        
        // Add image with pagination
        let yPosition = 30;
        let remainingHeight = imgHeight;
        let sourceY = 0;
        
        while (remainingHeight > 0) {
            const availableHeight = pageHeight - yPosition - PAGE_MARGIN;
            const chunkHeight = Math.min(availableHeight, remainingHeight);
            
            // Calculate source dimensions for this chunk
            const sourceHeight = (chunkHeight / imgHeight) * canvas.height;
            
            pdf.addImage(
                imgData, 'PNG',
                PAGE_MARGIN, yPosition,
                imgWidth, chunkHeight,
                undefined, 'FAST'
            );
            
            remainingHeight -= chunkHeight;
            sourceY += sourceHeight;
            
            if (remainingHeight > 0) {
                pdf.addPage();
                yPosition = PAGE_MARGIN;
            }
        }
        
        return savePDFWithLogging(pdf, filename);
    } catch (error) {
        console.error(`[PDF Export] Error exporting element ${elementId}:`, error);
        return { success: false, filename, error: error.message };
    }
}

/**
 * Generate AP Voucher PDF with structured layout
 */
export function generateVoucherPDF(voucher, fmt) {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = PAGE_MARGIN;
    
    // Header
    pdf.setFillColor(15, 42, 68); // #0F2A44
    pdf.rect(0, 0, pageWidth, 35, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(COMPANY_NAME, PAGE_MARGIN, 15);
    
    pdf.setFontSize(12);
    pdf.text('AP VOUCHER', PAGE_MARGIN, 25);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Voucher #: ${voucher.voucher_number}`, pageWidth - PAGE_MARGIN - 60, 15);
    pdf.text(`Date: ${voucher.voucher_date}`, pageWidth - PAGE_MARGIN - 60, 22);
    pdf.text(`Status: ${voucher.status.toUpperCase()}`, pageWidth - PAGE_MARGIN - 60, 29);
    
    y = 45;
    pdf.setTextColor(0, 0, 0);
    
    // Supplier Info
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Supplier Information', PAGE_MARGIN, y);
    y += LINE_HEIGHT;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Supplier ID: ${voucher.supplier_id}`, PAGE_MARGIN, y);
    y += LINE_HEIGHT - 2;
    pdf.text(`Supplier Name: ${voucher.supplier_name}`, PAGE_MARGIN, y);
    y += LINE_HEIGHT - 2;
    if (voucher.reference_number) {
        pdf.text(`Reference #: ${voucher.reference_number}`, PAGE_MARGIN, y);
        y += LINE_HEIGHT - 2;
    }
    if (voucher.due_date) {
        pdf.text(`Due Date: ${voucher.due_date}`, PAGE_MARGIN, y);
        y += LINE_HEIGHT - 2;
    }
    if (voucher.terms) {
        pdf.text(`Terms: ${voucher.terms}`, PAGE_MARGIN, y);
        y += LINE_HEIGHT - 2;
    }
    
    y += 5;
    
    // Particulars
    pdf.setFont('helvetica', 'bold');
    pdf.text('Particulars:', PAGE_MARGIN, y);
    y += LINE_HEIGHT - 2;
    pdf.setFont('helvetica', 'normal');
    const particularsLines = pdf.splitTextToSize(voucher.particulars || '-', pageWidth - (PAGE_MARGIN * 2));
    pdf.text(particularsLines, PAGE_MARGIN, y);
    y += (particularsLines.length * 5) + 5;
    
    // Line Items Table
    pdf.setFont('helvetica', 'bold');
    pdf.text('Line Items', PAGE_MARGIN, y);
    y += 3;
    
    // Table header
    pdf.setFillColor(240, 240, 240);
    pdf.rect(PAGE_MARGIN, y, pageWidth - (PAGE_MARGIN * 2), 8, 'F');
    pdf.setFontSize(9);
    pdf.text('Account', PAGE_MARGIN + 2, y + 5);
    pdf.text('Description', PAGE_MARGIN + 60, y + 5);
    pdf.text('Amount', pageWidth - PAGE_MARGIN - 25, y + 5);
    y += 10;
    
    // Table rows
    pdf.setFont('helvetica', 'normal');
    let totalAmount = 0;
    (voucher.lines || []).forEach((line, idx) => {
        if (idx % 2 === 1) {
            pdf.setFillColor(250, 250, 250);
            pdf.rect(PAGE_MARGIN, y - 4, pageWidth - (PAGE_MARGIN * 2), 7, 'F');
        }
        pdf.text(line.account_name || line.account_code || '-', PAGE_MARGIN + 2, y);
        pdf.text((line.description || '-').substring(0, 40), PAGE_MARGIN + 60, y);
        pdf.text(fmt ? fmt(line.amount) : `₱${(line.amount || 0).toLocaleString()}`, pageWidth - PAGE_MARGIN - 25, y);
        totalAmount += line.amount || 0;
        y += 7;
    });
    
    // Total row
    y += 2;
    pdf.setFillColor(15, 42, 68);
    pdf.rect(PAGE_MARGIN, y - 4, pageWidth - (PAGE_MARGIN * 2), 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TOTAL', PAGE_MARGIN + 2, y);
    pdf.text(fmt ? fmt(voucher.total_amount) : `₱${(voucher.total_amount || 0).toLocaleString()}`, pageWidth - PAGE_MARGIN - 25, y);
    y += 12;
    
    pdf.setTextColor(0, 0, 0);

    const withholdingAmount = Number(voucher.withholding_amount || 0);
    const netPayable = Number(voucher.net_payable ?? ((voucher.total_amount || 0) - withholdingAmount));
    if (withholdingAmount > 0) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Less: Expanded Withholding Tax', PAGE_MARGIN + 2, y);
        pdf.text(fmt ? fmt(withholdingAmount) : `PHP ${withholdingAmount.toLocaleString()}`, pageWidth - PAGE_MARGIN - 25, y);
        y += 6;
        pdf.setFont('helvetica', 'bold');
        pdf.text('NET PAYABLE', PAGE_MARGIN + 2, y);
        pdf.text(fmt ? fmt(netPayable) : `PHP ${netPayable.toLocaleString()}`, pageWidth - PAGE_MARGIN - 25, y);
        y += 10;
    }
    
    // GL Impact Summary
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text('GL Impact Summary', PAGE_MARGIN, y);
    y += 7;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    // Debit entries
    pdf.text('Debit:', PAGE_MARGIN, y);
    y += 5;
    (voucher.lines || []).forEach(line => {
        pdf.text(`  ${line.account_name || line.account_code}`, PAGE_MARGIN + 5, y);
        pdf.text(fmt ? fmt(line.amount) : `₱${(line.amount || 0).toLocaleString()}`, PAGE_MARGIN + 100, y);
        y += 5;
    });
    
    y += 3;
    pdf.text('Credit:', PAGE_MARGIN, y);
    y += 5;
    pdf.text('  Accounts Payable', PAGE_MARGIN + 5, y);
    pdf.text(fmt ? fmt(netPayable) : `PHP ${netPayable.toLocaleString()}`, PAGE_MARGIN + 100, y);
    y += 5;
    if (withholdingAmount > 0) {
        pdf.text(`  ${voucher.withholding_account || 'Expanded Withholding Tax Payable'}`, PAGE_MARGIN + 5, y);
        pdf.text(fmt ? fmt(withholdingAmount) : `PHP ${withholdingAmount.toLocaleString()}`, PAGE_MARGIN + 100, y);
    }
    
    // Footer
    y = pdf.internal.pageSize.getHeight() - 15;
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Generated by ${COMPANY_NAME} on ${new Date().toLocaleString()}`, PAGE_MARGIN, y);
    pdf.text(`Page 1 of 1`, pageWidth - PAGE_MARGIN - 20, y);
    
    const filename = `APV_${voucher.voucher_number}.pdf`;
    return savePDFWithLogging(pdf, filename);
}

/**
 * Generate AP Summary Report PDF
 */
export function generateAPSummaryPDF(data, filters, fmt) {
    const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape for wider tables
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = PAGE_MARGIN;
    
    // Header
    pdf.setFillColor(15, 42, 68);
    pdf.rect(0, 0, pageWidth, 30, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('AP SUMMARY REPORT', PAGE_MARGIN, 15);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${data.date_from} to ${data.date_to}`, PAGE_MARGIN, 23);
    pdf.text(`View: ${data.view === 'detailed' ? 'Detailed' : 'Summary'}`, pageWidth / 2, 15);
    pdf.text(`Status: ${data.status_filter || 'All'}`, pageWidth / 2, 23);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - PAGE_MARGIN - 60, 15);
    
    y = 40;
    pdf.setTextColor(0, 0, 0);
    
    // Totals Summary
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Summary', PAGE_MARGIN, y);
    y += 7;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    const totals = data.totals || {};
    pdf.text(`Gross Vouchered: ${fmt ? fmt(totals.total_gross_vouchered ?? totals.total_vouchered) : (totals.total_gross_vouchered ?? totals.total_vouchered)}`, PAGE_MARGIN, y);
    pdf.text(`WTAX: ${fmt ? fmt(totals.total_withholding || 0) : (totals.total_withholding || 0)}`, PAGE_MARGIN + 70, y);
    pdf.text(`Net Payable: ${fmt ? fmt(totals.total_net_payable ?? totals.total_vouchered) : (totals.total_net_payable ?? totals.total_vouchered)}`, PAGE_MARGIN + 115, y);
    pdf.text(`Outstanding: ${fmt ? fmt(totals.outstanding) : totals.outstanding}`, PAGE_MARGIN + 190, y);
    y += 10;
    
    // GL Reconciliation
    if (data.gl_reconciliation) {
        const recon = data.gl_reconciliation;
        pdf.setFont('helvetica', 'bold');
        pdf.text(`GL Reconciliation: ${recon.is_reconciled ? 'MATCHED' : 'MISMATCH'}`, PAGE_MARGIN, y);
        if (!recon.is_reconciled) {
            pdf.setFont('helvetica', 'normal');
            pdf.text(`Variance: ${fmt ? fmt(recon.variance) : recon.variance}`, PAGE_MARGIN + 80, y);
        }
        y += 10;
    }
    
    // Transactions Table
    pdf.setFont('helvetica', 'bold');
    pdf.text('Transactions', PAGE_MARGIN, y);
    y += 5;
    
    // Table header
    pdf.setFillColor(240, 240, 240);
    pdf.rect(PAGE_MARGIN, y, pageWidth - (PAGE_MARGIN * 2), 8, 'F');
    pdf.setFontSize(8);
    
    if (data.view === 'detailed') {
        pdf.text('Supplier', PAGE_MARGIN + 2, y + 5);
        pdf.text('Doc #', PAGE_MARGIN + 50, y + 5);
        pdf.text('Date', PAGE_MARGIN + 85, y + 5);
        pdf.text('Type', PAGE_MARGIN + 115, y + 5);
        pdf.text('Amount', PAGE_MARGIN + 140, y + 5);
        pdf.text('Status', PAGE_MARGIN + 175, y + 5);
    } else {
        pdf.text('Supplier', PAGE_MARGIN + 2, y + 5);
        pdf.text('Vouchers', PAGE_MARGIN + 80, y + 5);
        pdf.text('Payments', PAGE_MARGIN + 110, y + 5);
        pdf.text('Net Vouchered', PAGE_MARGIN + 140, y + 5);
        pdf.text('Total Paid', PAGE_MARGIN + 185, y + 5);
        pdf.text('Outstanding', PAGE_MARGIN + 220, y + 5);
    }
    y += 10;
    
    // Table rows
    pdf.setFont('helvetica', 'normal');
    const transactions = data.transactions || [];
    
    transactions.forEach((txn, idx) => {
        // Check for page break
        if (y > pageHeight - 30) {
            pdf.addPage();
            y = PAGE_MARGIN;
        }
        
        if (idx % 2 === 1) {
            pdf.setFillColor(250, 250, 250);
            pdf.rect(PAGE_MARGIN, y - 4, pageWidth - (PAGE_MARGIN * 2), 7, 'F');
        }
        
        if (data.view === 'detailed') {
            pdf.text((txn.supplier_name || '-').substring(0, 25), PAGE_MARGIN + 2, y);
            pdf.text(txn.doc_number || '-', PAGE_MARGIN + 50, y);
            pdf.text(txn.date || '-', PAGE_MARGIN + 85, y);
            pdf.text(txn.type || '-', PAGE_MARGIN + 115, y);
            pdf.text(fmt ? fmt(txn.amount) : String(txn.amount), PAGE_MARGIN + 140, y);
            pdf.text(txn.status || '-', PAGE_MARGIN + 175, y);
        } else {
            pdf.text((txn.supplier_name || '-').substring(0, 35), PAGE_MARGIN + 2, y);
            pdf.text(String(txn.voucher_count || 0), PAGE_MARGIN + 85, y);
            pdf.text(String(txn.payment_count || 0), PAGE_MARGIN + 115, y);
            pdf.text(fmt ? fmt(txn.total_vouchered) : String(txn.total_vouchered || 0), PAGE_MARGIN + 140, y);
            pdf.text(fmt ? fmt(txn.total_paid) : String(txn.total_paid || 0), PAGE_MARGIN + 185, y);
            pdf.text(fmt ? fmt(txn.outstanding) : String(txn.outstanding || 0), PAGE_MARGIN + 220, y);
        }
        y += 7;
    });
    
    // Footer on each page
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated by ${COMPANY_NAME}`, PAGE_MARGIN, pageHeight - 10);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - PAGE_MARGIN - 25, pageHeight - 10);
    }
    
    const filename = `AP_Summary_${data.date_from}_to_${data.date_to}.pdf`;
    return savePDFWithLogging(pdf, filename);
}

/**
 * Generate AP Aging Report PDF
 */
export function generateAPAgingPDF(data, fmt) {
    const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = PAGE_MARGIN;
    
    // Header
    pdf.setFillColor(15, 42, 68);
    pdf.rect(0, 0, pageWidth, 30, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('AP AGING REPORT', PAGE_MARGIN, 15);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`As of: ${data.as_of_date}`, PAGE_MARGIN, 23);
    pdf.text(`View: ${data.view === 'detailed' ? 'Detailed' : 'Summary'}`, pageWidth / 2, 15);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - PAGE_MARGIN - 60, 15);
    
    y = 40;
    pdf.setTextColor(0, 0, 0);
    
    // Bucket Totals
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Aging Buckets', PAGE_MARGIN, y);
    y += 7;
    
    const buckets = data.bucket_totals || {};
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    
    // Draw bucket boxes
    const boxWidth = 40;
    const boxHeight = 15;
    let bx = PAGE_MARGIN;
    
    const bucketData = [
        { label: 'Current', value: buckets.current, color: [16, 185, 129] },
        { label: '1-30 Days', value: buckets.days_1_30, color: [59, 130, 246] },
        { label: '31-60 Days', value: buckets.days_31_60, color: [245, 158, 11] },
        { label: '61-90 Days', value: buckets.days_61_90, color: [249, 115, 22] },
        { label: '90+ Days', value: buckets.days_90_plus, color: [239, 68, 68] },
        { label: 'TOTAL', value: buckets.total, color: [15, 42, 68] }
    ];
    
    bucketData.forEach(bucket => {
        pdf.setFillColor(...bucket.color);
        pdf.rect(bx, y, boxWidth, boxHeight, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(7);
        pdf.text(bucket.label, bx + 2, y + 5);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text(fmt ? fmt(bucket.value) : String(bucket.value || 0), bx + 2, y + 12);
        pdf.setFont('helvetica', 'normal');
        bx += boxWidth + 3;
    });
    
    y += boxHeight + 10;
    pdf.setTextColor(0, 0, 0);
    
    // Reconciliation
    if (data.reconciliation) {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Reconciliation: ${data.reconciliation.matches ? 'MATCHED ✓' : 'MISMATCH ✗'}`, PAGE_MARGIN, y);
        y += 8;
    }
    
    // Data Table
    pdf.setFont('helvetica', 'bold');
    pdf.text(data.view === 'detailed' ? 'Voucher Details' : 'By Supplier', PAGE_MARGIN, y);
    y += 5;
    
    // Table header
    pdf.setFillColor(240, 240, 240);
    pdf.rect(PAGE_MARGIN, y, pageWidth - (PAGE_MARGIN * 2), 8, 'F');
    pdf.setFontSize(8);
    
    if (data.view === 'detailed') {
        pdf.text('Supplier', PAGE_MARGIN + 2, y + 5);
        pdf.text('Voucher #', PAGE_MARGIN + 45, y + 5);
        pdf.text('Due Date', PAGE_MARGIN + 80, y + 5);
        pdf.text('Net Payable', PAGE_MARGIN + 110, y + 5);
        pdf.text('Paid', PAGE_MARGIN + 140, y + 5);
        pdf.text('Outstanding', PAGE_MARGIN + 170, y + 5);
        pdf.text('Days', PAGE_MARGIN + 205, y + 5);
        pdf.text('Bucket', PAGE_MARGIN + 225, y + 5);
    } else {
        pdf.text('Supplier', PAGE_MARGIN + 2, y + 5);
        pdf.text('#', PAGE_MARGIN + 55, y + 5);
        pdf.text('Current', PAGE_MARGIN + 70, y + 5);
        pdf.text('1-30', PAGE_MARGIN + 100, y + 5);
        pdf.text('31-60', PAGE_MARGIN + 130, y + 5);
        pdf.text('61-90', PAGE_MARGIN + 160, y + 5);
        pdf.text('90+', PAGE_MARGIN + 190, y + 5);
        pdf.text('Total', PAGE_MARGIN + 220, y + 5);
    }
    y += 10;
    
    // Table rows
    pdf.setFont('helvetica', 'normal');
    const rows = data.rows || [];
    
    rows.forEach((row, idx) => {
        if (y > pageHeight - 30) {
            pdf.addPage();
            y = PAGE_MARGIN;
        }
        
        if (idx % 2 === 1) {
            pdf.setFillColor(250, 250, 250);
            pdf.rect(PAGE_MARGIN, y - 4, pageWidth - (PAGE_MARGIN * 2), 7, 'F');
        }
        
        if (data.view === 'detailed') {
            pdf.text((row.supplier_name || '-').substring(0, 20), PAGE_MARGIN + 2, y);
            pdf.text(row.voucher_number || '-', PAGE_MARGIN + 45, y);
            pdf.text(row.due_date || '-', PAGE_MARGIN + 80, y);
            pdf.text(fmt ? fmt(row.voucher_amount) : String(row.voucher_amount || 0), PAGE_MARGIN + 110, y);
            pdf.text(fmt ? fmt(row.amount_paid) : String(row.amount_paid || 0), PAGE_MARGIN + 140, y);
            pdf.text(fmt ? fmt(row.outstanding) : String(row.outstanding || 0), PAGE_MARGIN + 170, y);
            pdf.text(String(row.days_past_due || 0), PAGE_MARGIN + 208, y);
            pdf.text(row.bucket_label || '-', PAGE_MARGIN + 225, y);
        } else {
            pdf.text((row.supplier_name || '-').substring(0, 25), PAGE_MARGIN + 2, y);
            pdf.text(String(row.voucher_count || 0), PAGE_MARGIN + 58, y);
            pdf.text(fmt ? fmt(row.current) : String(row.current || 0), PAGE_MARGIN + 70, y);
            pdf.text(fmt ? fmt(row.days_1_30) : String(row.days_1_30 || 0), PAGE_MARGIN + 100, y);
            pdf.text(fmt ? fmt(row.days_31_60) : String(row.days_31_60 || 0), PAGE_MARGIN + 130, y);
            pdf.text(fmt ? fmt(row.days_61_90) : String(row.days_61_90 || 0), PAGE_MARGIN + 160, y);
            pdf.text(fmt ? fmt(row.days_90_plus) : String(row.days_90_plus || 0), PAGE_MARGIN + 190, y);
            pdf.text(fmt ? fmt(row.total) : String(row.total || 0), PAGE_MARGIN + 220, y);
        }
        y += 7;
    });
    
    // Footer
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated by ${COMPANY_NAME}`, PAGE_MARGIN, pageHeight - 10);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - PAGE_MARGIN - 25, pageHeight - 10);
    }
    
    const filename = `AP_Aging_${data.as_of_date}.pdf`;
    return savePDFWithLogging(pdf, filename);
}

function writeReportHeader(pdf, title, subtitle) {
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFillColor(15, 42, 68);
    pdf.rect(0, 0, pageWidth, 30, 'F');

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, PAGE_MARGIN, 15);

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(subtitle || '', PAGE_MARGIN, 23);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - PAGE_MARGIN - 60, 15);

    pdf.setTextColor(0, 0, 0);
}

function writeSectionTable(pdf, y, title, items, amountLabel, fmt, options = {}) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const showComparison = options.showComparison || false;
    const comparisonLabel = options.comparisonLabel || 'Comparison';
    const varianceLabel = options.varianceLabel || 'Variance';

    if (y > pageHeight - 45) {
        pdf.addPage();
        y = PAGE_MARGIN;
    }

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, PAGE_MARGIN, y);
    y += 5;

    pdf.setFillColor(240, 240, 240);
    pdf.rect(PAGE_MARGIN, y, pageWidth - (PAGE_MARGIN * 2), 8, 'F');
    pdf.setFontSize(8);
    pdf.text('Code', PAGE_MARGIN + 2, y + 5);
    pdf.text('Account', PAGE_MARGIN + 25, y + 5);
    pdf.text(amountLabel, PAGE_MARGIN + 115, y + 5);
    if (showComparison) {
        pdf.text(comparisonLabel, PAGE_MARGIN + 155, y + 5);
        pdf.text(varianceLabel, PAGE_MARGIN + 195, y + 5);
    }
    y += 10;

    pdf.setFont('helvetica', 'normal');
    const rows = items || [];

    if (rows.length === 0) {
        pdf.text('No accounts with balances', PAGE_MARGIN + 2, y);
        y += 8;
        return y;
    }

    rows.forEach((item, idx) => {
        if (y > pageHeight - 20) {
            pdf.addPage();
            y = PAGE_MARGIN;
        }

        if (idx % 2 === 1) {
            pdf.setFillColor(250, 250, 250);
            pdf.rect(PAGE_MARGIN, y - 4, pageWidth - (PAGE_MARGIN * 2), 7, 'F');
        }

        pdf.text(String(item.account_code || '-'), PAGE_MARGIN + 2, y);
        pdf.text(String(item.account_name || '-').substring(0, 42), PAGE_MARGIN + 25, y);
        pdf.text(fmt ? fmt(item.current_balance || 0) : String(item.current_balance || 0), PAGE_MARGIN + 115, y);

        if (showComparison) {
            const comparison = item.comparison_balance || 0;
            const variance = item.variance ?? item.pct_change ?? 0;
            const varianceSuffix = item.pct_change != null ? '%' : '';
            pdf.text(fmt ? fmt(comparison) : String(comparison), PAGE_MARGIN + 155, y);
            pdf.text(`${variance > 0 ? '+' : ''}${variance}${varianceSuffix}`, PAGE_MARGIN + 195, y);
        }

        y += 7;
    });

    return y + 4;
}

export function generateIncomeStatementPDF(data, filters, reportType, compareType, fmt) {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = months[(filters?.month || 1) - 1] || '';
    const subtitle =
        reportType === 'period' ? `For ${monthName} ${filters?.year}` :
        reportType === 'comparative' ? `${monthName} ${filters?.year} vs ${compareType === 'prev_month' ? 'Previous Month' : 'Same Month Last Year'}` :
        reportType === 'ytd' ? `YTD ${filters?.year} through ${monthName}` :
        `${data.period_start} to ${data.period_end}`;

    writeReportHeader(pdf, 'INCOME STATEMENT', subtitle);

    let y = 40;
    const showComparison = reportType === 'comparative' || reportType === 'ytd';
    y = writeSectionTable(pdf, y, 'REVENUES', data.sections?.revenues || [], 'Current', fmt, {
        showComparison,
        varianceLabel: '% Change',
    });
    y = writeSectionTable(pdf, y, 'EXPENSES', data.sections?.expenses || [], 'Current', fmt, {
        showComparison,
        varianceLabel: '% Change',
    });

    if (y > 235) {
        pdf.addPage();
        y = PAGE_MARGIN;
    }

    const totals = data.totals || {};
    pdf.setFillColor(data.is_profit ? 236 : 254, data.is_profit ? 253 : 242, data.is_profit ? 245 : 242);
    pdf.rect(PAGE_MARGIN, y, pageWidth - (PAGE_MARGIN * 2), 34, 'F');
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Summary', PAGE_MARGIN + 4, y + 8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Total Revenue: ${fmt ? fmt(totals.total_revenue || 0) : totals.total_revenue || 0}`, PAGE_MARGIN + 4, y + 16);
    pdf.text(`Total Expenses: ${fmt ? fmt(totals.total_expenses || 0) : totals.total_expenses || 0}`, PAGE_MARGIN + 4, y + 23);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${data.is_profit ? 'Net Income' : 'Net Loss'}: ${fmt ? fmt(Math.abs(totals.net_income || 0)) : Math.abs(totals.net_income || 0)}`, PAGE_MARGIN + 110, y + 23);

    const filename = `Income_Statement_${new Date().toISOString().split('T')[0]}.pdf`;
    return savePDFWithLogging(pdf, filename);
}

export function generateBalanceSheetPDF(data, filters, reportType, compareType, fmt) {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = months[(filters?.month || 1) - 1] || '';
    const subtitle = reportType === 'comparative'
        ? `${monthName} ${filters?.year} vs ${compareType === 'prev_month' ? 'Previous Month' : compareType === 'dec_last_year' ? `December ${filters?.year - 1}` : `Same Month Last Year`}`
        : `As of ${data.as_of_date || filters?.date || filters?.dateTo}`;

    writeReportHeader(pdf, 'BALANCE SHEET', subtitle);

    let y = 40;
    const showComparison = reportType === 'comparative';
    y = writeSectionTable(pdf, y, 'ASSETS', data.sections?.assets || [], 'Current Balance', fmt, { showComparison });
    y = writeSectionTable(pdf, y, 'LIABILITIES', data.sections?.liabilities || [], 'Current Balance', fmt, { showComparison });
    y = writeSectionTable(pdf, y, 'EQUITY', data.sections?.equity || [], 'Current Balance', fmt, { showComparison });

    if (y > 235) {
        pdf.addPage();
        y = PAGE_MARGIN;
    }

    const totals = data.totals || {};
    pdf.setFillColor(248, 250, 252);
    pdf.rect(PAGE_MARGIN, y, pageWidth - (PAGE_MARGIN * 2), 42, 'F');
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Summary', PAGE_MARGIN + 4, y + 8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Total Assets: ${fmt ? fmt(totals.total_assets || 0) : totals.total_assets || 0}`, PAGE_MARGIN + 4, y + 16);
    pdf.text(`Total Liabilities: ${fmt ? fmt(totals.total_liabilities || 0) : totals.total_liabilities || 0}`, PAGE_MARGIN + 4, y + 23);
    pdf.text(`Total Equity: ${fmt ? fmt(totals.total_equity || 0) : totals.total_equity || 0}`, PAGE_MARGIN + 4, y + 30);
    pdf.setFont('helvetica', 'bold');
    pdf.text(data.is_balanced ? 'Status: Balanced' : `Status: Imbalanced by ${fmt ? fmt(data.imbalance || 0) : data.imbalance || 0}`, PAGE_MARGIN + 110, y + 30);

    const filename = `Balance_Sheet_${new Date().toISOString().split('T')[0]}.pdf`;
    return savePDFWithLogging(pdf, filename);
}

export default {
    exportElementToPDF,
    generateVoucherPDF,
    generateAPSummaryPDF,
    generateAPAgingPDF,
    generateIncomeStatementPDF,
    generateBalanceSheetPDF
};
